/**
 * PatchModalController - Handles patch-related modal dialogs
 * 
 * Manages New Patch, Save As, and Patch Settings modals.
 * Modal UI logic (show/close/keyboard) lives here, actual state mutations
 * are performed via injected callbacks.
 */

import { MODAL_IDS } from '../constants/modals.js';
import { showToast } from './ToastController.js';
import { eventBus, EVENTS } from '../services/EventBus.js';

// Module state - injected via init
let storageService = null;
let recentPatchesService = null;
let getCurrentPatchNameFn = null;
let setCurrentPatchNameFn = null;
let getPatchSettingsFn = null;
let setPatchSettingsFn = null;
let createNewPatchFn = null;
let renderAllCablesFn = null;
let scheduleAutosaveFn = null;
let getInstanceCountFn = null;
let getConnectionCountFn = null;
let invalidatePatchesCacheFn = null;

/**
 * Initialize the PatchModalController
 * @param {Object} deps - Dependencies
 * @param {Object} deps.storageService - Storage service instance
 * @param {Object} deps.recentPatchesService - Recent patches service
 * @param {Function} deps.getCurrentPatchName - Get current patch name
 * @param {Function} deps.setCurrentPatchName - Set current patch name
 * @param {Function} deps.getPatchSettings - Get patch settings object
 * @param {Function} deps.setPatchSettings - Set patch settings (cableStyle, etc.)
 * @param {Function} deps.createNewPatch - Callback to create a new blank patch
 * @param {Function} deps.renderAllCables - Callback to re-render cables
 * @param {Function} deps.scheduleAutosave - Callback to trigger autosave
 * @param {Function} deps.getInstanceCount - Get device instance count
 * @param {Function} deps.getConnectionCount - Get connection count
 * @param {Function} deps.invalidatePatchesCache - Invalidate patches cache
 * @returns {Object} Public methods
 */
export function initPatchModalController(deps) {
    storageService = deps.storageService;
    recentPatchesService = deps.recentPatchesService;
    getCurrentPatchNameFn = deps.getCurrentPatchName;
    setCurrentPatchNameFn = deps.setCurrentPatchName;
    getPatchSettingsFn = deps.getPatchSettings;
    setPatchSettingsFn = deps.setPatchSettings;
    createNewPatchFn = deps.createNewPatch;
    renderAllCablesFn = deps.renderAllCables;
    scheduleAutosaveFn = deps.scheduleAutosave;
    getInstanceCountFn = deps.getInstanceCount;
    getConnectionCountFn = deps.getConnectionCount;
    invalidatePatchesCacheFn = deps.invalidatePatchesCache;
    
    return {
        showNewPatchModal,
        closeNewPatchModal,
        confirmNewPatch,
        showSaveAsModal,
        closeSaveAsModal,
        confirmSaveAs,
        showPatchSettingsModal,
        closePatchSettingsModal,
        savePatchSettingsModal
    };
}

// ============= NEW PATCH MODAL =============

/**
 * Show the New Patch modal
 */
function showNewPatchModal() {
    const modal = document.getElementById(MODAL_IDS.NEW_PATCH);
    const input = document.getElementById('new-patch-name-input');
    
    if (!modal || !input) return;
    
    input.value = '';
    modal.classList.add('active');
    input.focus();
    
    // Add keyboard listeners
    input.onkeydown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            confirmNewPatch();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            closeNewPatchModal();
        }
    };
}

/**
 * Close the New Patch modal
 */
function closeNewPatchModal() {
    const input = document.getElementById('new-patch-name-input');
    if (input) input.onkeydown = null;
    
    const modal = document.getElementById(MODAL_IDS.NEW_PATCH);
    if (modal) modal.classList.remove('active');
}

/**
 * Confirm the New Patch modal - create the new patch
 */
function confirmNewPatch() {
    const input = document.getElementById('new-patch-name-input');
    const trimmedName = input?.value.trim() || 'Untitled Patch';
    
    closeNewPatchModal();
    
    // Delegate to app.js callback for actual state reset
    if (createNewPatchFn) {
        createNewPatchFn(trimmedName);
    }
}

// ============= SAVE AS MODAL =============

/**
 * Show the Save As modal
 */
function showSaveAsModal() {
    const modal = document.getElementById(MODAL_IDS.SAVE_AS);
    const input = document.getElementById('save-as-name-input');
    
    if (!modal || !input) return;
    
    // Default to current name (unless it's Untitled Patch)
    const currentName = getCurrentPatchNameFn ? getCurrentPatchNameFn() : 'Untitled Patch';
    const defaultName = currentName === 'Untitled Patch' ? '' : currentName;
    input.value = defaultName;
    modal.classList.add('active');
    input.focus();
    input.select();
    
    // Add keyboard listeners
    input.onkeydown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            confirmSaveAs();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            closeSaveAsModal();
        }
    };
}

/**
 * Close the Save As modal
 */
function closeSaveAsModal() {
    const input = document.getElementById('save-as-name-input');
    if (input) input.onkeydown = null;
    
    const modal = document.getElementById(MODAL_IDS.SAVE_AS);
    if (modal) modal.classList.remove('active');
}

/**
 * Confirm the Save As modal - save with new name
 */
async function confirmSaveAs() {
    const input = document.getElementById('save-as-name-input');
    const trimmedName = input?.value.trim();
    
    if (!trimmedName) {
        showToast('Please enter a patch name', true);
        return;
    }
    
    closeSaveAsModal();
    
    // Save the patch with the new name
    const result = await storageService.savePatch(trimmedName, true); // forceInsert = true for new entry
    
    if (result.success) {
        if (setCurrentPatchNameFn) setCurrentPatchNameFn(trimmedName);
        
        if (recentPatchesService) {
            recentPatchesService.addToRecentPatches(
                result.id || '',
                trimmedName,
                Date.now(),
                getInstanceCountFn ? getInstanceCountFn() : 0,
                getConnectionCountFn ? getConnectionCountFn() : 0
            );
        }
        
        if (invalidatePatchesCacheFn) invalidatePatchesCacheFn();
        
        showToast(`Saved as "${trimmedName}"`);
        
        // Emit patch saved event
        eventBus.emit(EVENTS.PATCH_SAVED, { patchName: trimmedName });
    } else {
        showToast('Failed to save patch', true);
    }
}

// ============= PATCH SETTINGS MODAL =============

/**
 * Show the Patch Settings modal
 */
function showPatchSettingsModal() {
    const modal = document.getElementById(MODAL_IDS.PATCH_SETTINGS);
    const nameInput = document.getElementById('patch-settings-name');
    const cableStyleSelect = document.getElementById('patch-settings-cable-style');
    
    if (!modal || !nameInput) return;
    
    // Populate current values
    const currentName = getCurrentPatchNameFn ? getCurrentPatchNameFn() : 'Untitled Patch';
    nameInput.value = currentName;
    
    // Set cable style
    const settings = getPatchSettingsFn ? getPatchSettingsFn() : { cableStyle: 'curvy' };
    if (cableStyleSelect) {
        cableStyleSelect.value = settings.cableStyle || 'curvy';
    }
    
    modal.classList.add('active');
    nameInput.focus();
    nameInput.select();
    
    // Keyboard handler
    nameInput.onkeydown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            savePatchSettingsModal();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            closePatchSettingsModal();
        }
    };
}

/**
 * Close the Patch Settings modal
 */
function closePatchSettingsModal() {
    const nameInput = document.getElementById('patch-settings-name');
    if (nameInput) nameInput.onkeydown = null;
    
    const modal = document.getElementById(MODAL_IDS.PATCH_SETTINGS);
    if (modal) modal.classList.remove('active');
}

/**
 * Save the Patch Settings modal
 */
function savePatchSettingsModal() {
    const nameInput = document.getElementById('patch-settings-name');
    const cableStyleSelect = document.getElementById('patch-settings-cable-style');
    
    const newName = nameInput?.value.trim() || 'Untitled Patch';
    const newCableStyle = cableStyleSelect?.value || 'curvy';
    
    // Update patch name
    const currentName = getCurrentPatchNameFn ? getCurrentPatchNameFn() : '';
    if (newName !== currentName && setCurrentPatchNameFn) {
        setCurrentPatchNameFn(newName);
    }
    
    // Update cable style and check if changed
    const currentSettings = getPatchSettingsFn ? getPatchSettingsFn() : { cableStyle: 'curvy' };
    const styleChanged = currentSettings.cableStyle !== newCableStyle;
    
    if (setPatchSettingsFn) {
        setPatchSettingsFn({ cableStyle: newCableStyle });
    }
    
    // Re-render cables if style changed
    if (styleChanged && renderAllCablesFn) {
        renderAllCablesFn();
    }
    
    closePatchSettingsModal();
    showToast('Patch settings updated');
    
    // Trigger autosave to persist settings
    if (scheduleAutosaveFn) {
        scheduleAutosaveFn();
    }
}
